import React from 'react'

// @src imports
import useActions from 'src/hooks/useActions'
import { Button, Confirm } from 'src/chrome'

import {
  useAccount,
  useEffect,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'

import {
  CardFragment,
  CardType,
  getLayout,
  getLayouts,
  LayoutCategory,
  LayoutDimensions,
  Owner,
} from 'src/graphql'

import { EditorElement } from 'src/editor/EditorCard'

// relative imports
import layoutStyles from './layouts.module.scss'
import styles from '../../styles/components/Drawer.module.scss'
import { Api } from '../../api'
import { Steps } from '../../types'
import { Memoized } from 'src/hooks/dependencies'

type Props = {
  api: Api & { step: { type: Steps.EditLayout } }
  onChangeTransitionType: Memoized<
    (shouldUseRegularTransition: boolean) => void
  >
}

const Layouts: React.FC<Props> = props => {
  const { onChangeTransitionType } = props
  const {
    card,
    fullBleeds,
    step,
    updatePanel,
    selectedLayoutId,
    setSelectedLayoutId,
    isCreatingBackPanel,
    setIsCreatingBackPanel,
  } = props.api
  const { fullBleedIndex, panelIndex } = step

  const actions = useActions()

  const mutations = useMutations()

  const account = useAccount()

  const defaultBackPanel = account.settings?.defaultBackPanel ?? null

  const [defaultBackPanelId, setDefaultBackPanelId] = useState<
    string | undefined
  >(defaultBackPanel?.id)
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false)
  const [currentDeleteLayoutId, setCurrentDeleteLayoutId] = useState<
    string | null
  >(null)
  const [currentLayoutId, setCurrentLayoutId] = useState<string>()

  const isBackPanel =
    fullBleeds.length > 2 && fullBleedIndex === fullBleeds.length - 1

  const category = isBackPanel
    ? LayoutCategory.BACK_PANELS
    : LayoutCategory.BASIC_LAYOUTS
  const dimensions = isBackPanel
    ? LayoutDimensions.VERTICAL
    : getDimensions(card)

  const [layouts, userBackPanels, layout] = useQueries(
    getLayouts({ category, dimensions, owner: Owner.SYSTEM }),
    getLayouts({
      category: LayoutCategory.BACK_PANELS,
      dimensions: LayoutDimensions.VERTICAL,
      owner: Owner.USER,
    }),
    selectedLayoutId !== undefined
      ? getLayout({ id: selectedLayoutId })
      : undefined,
  )

  const currentLayouts =
    !isCreatingBackPanel && isBackPanel ? userBackPanels : layouts

  const toggleBackPanels = () => setIsCreatingBackPanel(!isCreatingBackPanel)

  const handleSelectLayout = (id: string) => {
    onChangeTransitionType(true)
    setSelectedLayoutId(id)
  }

  useEffect(() => {
    if (
      selectedLayoutId !== undefined &&
      selectedLayoutId !== currentLayoutId &&
      layout
    ) {
      onChangeTransitionType(false)
      setSelectedLayoutId(selectedLayoutId)
      setCurrentLayoutId(selectedLayoutId)

      if (!isBackPanel) {
        updatePanel(fullBleedIndex, panelIndex, panel => {
          const elementImages = panel.elements
            .filter(element => element.image)
            .map(element => element.image)
          const elementTexts = panel.elements
            .filter(element => element.text)
            .map(element => element.text)
          return {
            ...panel,
            elements: layout.panel.elements
              .map(EditorElement)
              .map((element: EditorElement) => {
                if (element.image) {
                  return element.image.image
                    ? element
                    : {
                        ...element,
                        image:
                          elementImages.length > 0
                            ? // tslint:disable-next-line:no-array-mutation
                              elementImages.shift() || null
                            : element.image,
                      }
                } else {
                  return element.text && element.text.length > 1
                    ? element
                    : {
                        ...element,
                        text:
                          elementTexts.length > 0
                            ? // tslint:disable-next-line:no-array-mutation
                              elementTexts.shift() || null
                            : element.text,
                      }
                }
              }),
          }
        })
      } else {
        updatePanel(fullBleedIndex, panelIndex, panel => ({
          ...panel,
          backgroundColor: layout.panel.backgroundColor,
          elements: layout.panel.elements.map(EditorElement),
        }))
      }
    }
  }, [
    selectedLayoutId,
    setSelectedLayoutId,
    currentLayoutId,
    layout,
    actions,
    isBackPanel,
    onChangeTransitionType,
    updatePanel,
    fullBleedIndex,
    panelIndex,
  ])

  const closeConfirm = () => {
    setIsDeleteConfirmVisible(false)
    setCurrentDeleteLayoutId(null)
  }

  const deleteLayout = () => {
    if (!currentDeleteLayoutId) {
      return
    }
    mutations.deleteLayout({ id: currentDeleteLayoutId })
    closeConfirm()
  }

  const showConfirmDelete = (id: string) => {
    setIsDeleteConfirmVisible(true)
    setCurrentDeleteLayoutId(id)
  }

  const setBackPanelDefaultLayout = async (id: string) => {
    setDefaultBackPanelId(id)
    const result = await mutations.updateAccount({
      account: { settings: { defaultBackPanelId: id } },
    })
    actions.updatedAccount(result.updateAccount.account)
  }

  const { isMobile } = useSelector(state => state.window)

  return (
    <div
      className={
        dimensions === LayoutDimensions.HORIZONTAL
          ? `${styles.images} ${styles.horizontal}`
          : styles.images
      }
    >
      {isBackPanel && (
        <div>
          {isCreatingBackPanel ? (
            <h4 style={{ fontSize: 15, marginBottom: 15 }}>
              {userBackPanels.length > 0
                ? 'Create new custom back panel '
                : 'Create a custom back panel '}
              {userBackPanels.length > 0 && (
                <span>
                  or{' '}
                  <span style={{ color: '#fc5a96' }} onClick={toggleBackPanels}>
                    select existing
                  </span>
                </span>
              )}
            </h4>
          ) : (
            <h4 style={{ fontSize: 15, marginBottom: 15 }}>
              Select an existing panel or{' '}
              <span style={{ color: '#fc5a96' }} onClick={toggleBackPanels}>
                create a new one
              </span>
            </h4>
          )}
        </div>
      )}
      <div id="layouts_container" className={styles.layouts}>
        {currentLayouts.map((layout, i) => (
          <div
            id={`layout_${i}`}
            className={`${layoutStyles.closeXHoverParent} ${styles.layoutContainer}`}
            key={layout.id}
            title={`Layout #${layout.id}`}
            style={{
              backgroundImage: `url(${layout.thumbnail.url})`,
              backgroundSize: 'cover',
              minWidth: 120,
              width: 120,
              height: dimensions === LayoutDimensions.POSTCARD ? 140 : 168,
              marginBottom: isMobile ? 15 : 5,
            }}
            onClick={() => {
              handleSelectLayout(layout.id)
            }}
          >
            {category === LayoutCategory.BACK_PANELS && !isCreatingBackPanel && (
              <div>
                {defaultBackPanelId !== layout.id && [
                  <div
                    key={1}
                    className={layoutStyles.closeXButton}
                    onClick={() => showConfirmDelete(layout.id)}
                  >
                    x
                  </div>,
                  <div
                    key={2}
                    className={styles.defaultButton}
                    onClick={() => setBackPanelDefaultLayout(layout.id)}
                  >
                    set as default
                  </div>,
                ]}
                {defaultBackPanelId === layout.id && (
                  <div
                    className={styles.defaultLayout}
                    onClick={() => {
                      handleSelectLayout(layout.id)
                    }}
                  >
                    <div className={styles.defaultLabel}>Default</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {category === LayoutCategory.BACK_PANELS && (
        <div>
          <div className={`${styles.drawerBarBottom} ${styles.layout}`}>
            <div className={styles.choices}>
              {(!isCreatingBackPanel ||
                (isCreatingBackPanel && !!userBackPanels.length)) && (
                <Button
                  id="create_or_choose_btn"
                  title={
                    !isCreatingBackPanel ? 'Create New' : 'Choose Existing'
                  }
                  buttonColor={'pink'}
                  style={{ marginRight: '5px' }}
                  icon={'GRID'}
                  iconSize={13}
                  iconColor={'#fff'}
                  onClick={toggleBackPanels}
                />
              )}
            </div>
          </div>
          {isDeleteConfirmVisible && (
            <Confirm
              title={'Delete Layout'}
              message={
                'Are you sure you want to delete this custom back panel layout?'
              }
              onConfirm={deleteLayout}
              onDecline={closeConfirm}
              confirmTitle={'Delete'}
            />
          )}
        </div>
      )}
    </div>
  )
}

const getDimensions = (card: CardFragment): LayoutDimensions => {
  if (card.type === CardType.POSTCARD) {
    return LayoutDimensions.POSTCARD
  } else if (card.isHorizontal) {
    return LayoutDimensions.HORIZONTAL
  } else {
    return LayoutDimensions.VERTICAL
  }
}

export default Layouts
